import { Effect } from 'effect';
import { PDF_TO_LINK_MAX_BYTES } from '../runners/pdf-to-link-support';

/**
 * 'Get a share link' handoff (W.4.124, F7): carries one tool's PDF output into
 * /tools/pdf-to-link through in-app navigation, so the visitor does not
 * download the file and upload it again.
 *
 * - One in-memory slot. A new stash replaces the old one; nothing is written
 *   to storage and nothing goes in the URL except the source tool's slug.
 * - The slot expires after SHARE_HANDOFF_TTL_MS. A reload empties it, and the
 *   pdf-to-link runner then shows its normal dropzone.
 * - Only outputs up to SHARE_LINK_MAX_BYTES are offered; the host API refuses
 *   anything larger, so the shell does not keep those bytes at all.
 *
 * The producer is the success panel's button; the consumer is the pdf-to-link
 * runner, which peeks the slot on mount, stages the file and clears the slot.
 */

/** Same cap as the PDF to Link upload (and the server's guest limit). */
export const SHARE_LINK_MAX_BYTES = PDF_TO_LINK_MAX_BYTES;
export const SHARE_HANDOFF_TTL_MS = 10 * 60 * 1000;

/** Runners hand over pdf-lib bytes or a Blob, whichever they downloaded. */
export type ShareablePdfBytes = Blob | Uint8Array;

export interface ShareHandoff {
  file: File;
  sourceTool: string;
  createdAt: number;
}

export interface StashShareHandoffInput {
  pdf: ShareablePdfBytes;
  fileName: string;
  sourceTool: string;
  now?: number;
}

let slot: ShareHandoff | null = null;

/** Byte length of either shape, without reading the bytes. */
export function shareablePdfByteLength(pdf: ShareablePdfBytes): number {
  return ArrayBuffer.isView(pdf) ? pdf.byteLength : pdf.size;
}

/** The host API takes only *.pdf names; a tool's output name may lack one. */
function ensurePdfName(fileName: string): string {
  const name = fileName.trim() || 'document';
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
}

function toPdfFile(pdf: ShareablePdfBytes, fileName: string): File {
  // ArrayBuffer.isView rather than instanceof Blob: it holds across realms.
  // slice() copies the view's exact bytes, so a view into a larger buffer
  // never carries the rest of that buffer along.
  const part: BlobPart = ArrayBuffer.isView(pdf) ? (pdf.slice().buffer as ArrayBuffer) : pdf;
  return new File([part], ensurePdfName(fileName), { type: 'application/pdf' });
}

/** Put a tool's output in the slot, replacing whatever was there. */
export const stashShareHandoffEffect = Effect.fn('tools.shareHandoff.stash')(
  (input: StashShareHandoffInput) =>
    Effect.sync((): ShareHandoff => {
      const handoff: ShareHandoff = {
        file: toPdfFile(input.pdf, input.fileName),
        sourceTool: input.sourceTool,
        createdAt: input.now ?? Date.now(),
      };
      slot = handoff;
      return handoff;
    }),
);

export function stashShareHandoff(input: StashShareHandoffInput): ShareHandoff {
  return Effect.runSync(stashShareHandoffEffect(input));
}

/**
 * Read the slot without taking it, so a remount (StrictMode, a re-render of
 * the runner) still finds the file. An expired slot is cleared and reads as
 * empty.
 */
export const peekShareHandoffEffect = Effect.fn('tools.shareHandoff.peek')((now: number) =>
  Effect.sync((): ShareHandoff | null => {
    const current = slot;
    if (current === null) return null;
    if (now - current.createdAt > SHARE_HANDOFF_TTL_MS) {
      slot = null;
      return null;
    }
    return current;
  }),
);

export function peekShareHandoff(now: number = Date.now()): ShareHandoff | null {
  return Effect.runSync(peekShareHandoffEffect(now));
}

export function clearShareHandoff(): void {
  slot = null;
}

/** In-app path to PDF to Link; `from` carries the slug only, never file data. */
export function shareLinkHandoffHref(linkBase: string, sourceTool: string): string {
  return `${linkBase.replace(/\/$/, '')}/pdf-to-link?from=${encodeURIComponent(sourceTool)}`;
}

/**
 * One decimal, 1024-based, matching the 5 MB cap's own units: '6.0 MB'.
 * Rounded up, so a PDF just over the cap never reads as '5.0 MB'.
 */
export function formatShareSize(bytes: number): string {
  const mib = 1024 * 1024;
  if (bytes >= mib) return `${(Math.ceil((bytes / mib) * 10) / 10).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}
